import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { 
  CodeIcon, 
  Truck, 
  Construction, 
  ServerIcon, 
  FactoryIcon,
  HeartPulseIcon,
  UsersIcon,
  GraduationCapIcon
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function Programs() {
  const trainingPrograms = [
    {
      title: "Software Development",
      description: "12-16 week intensive bootcamp in web development, mobile app creation, and software engineering fundamentals. Participants build real projects for their portfolios.",
      icon: <CodeIcon className="h-6 w-6 text-primary-600" />,
      categories: ["In-house", "High Demand", "Tech"],
      duration: "12-16 weeks"
    },
    {
      title: "Commercial Truck Driving",
      description: "CDL preparation and certification with job placement assistance. Includes simulator training, road practice, and DOT certification preparation.",
      icon: <Truck className="h-6 w-6 text-primary-600" />,
      categories: ["Partner", "High Demand", "Logistics"],
      duration: "6-8 weeks"
    },
    {
      title: "Construction Trades",
      description: "Hands-on training in carpentry, electrical, plumbing, and general construction. Incorporates OSHA certification and apprenticeship preparation.",
      icon: <Construction className="h-6 w-6 text-primary-600" />,
      categories: ["In-house", "High Demand", "Trades"],
      duration: "10-12 weeks"
    },
    {
      title: "IT Support & Networking",
      description: "Training for IT support roles with industry certification preparation (CompTIA A+, Network+). Includes real-world troubleshooting scenarios.",
      icon: <ServerIcon className="h-6 w-6 text-primary-600" />,
      categories: ["In-house", "Tech"],
      duration: "8-10 weeks"
    },
    {
      title: "Manufacturing & Production",
      description: "Skills development for modern manufacturing settings. Includes safety, quality control, basic machinery operation, and manufacturing technologies.",
      icon: <FactoryIcon className="h-6 w-6 text-primary-600" />,
      categories: ["Partner", "Entry Level"],
      duration: "4-6 weeks"
    },
    {
      title: "Healthcare Support",
      description: "Training for roles such as patient care technician, medical administrative assistant, and other healthcare support positions with certification preparation.",
      icon: <HeartPulseIcon className="h-6 w-6 text-primary-600" />,
      categories: ["Partner", "High Demand", "Healthcare"],
      duration: "8-12 weeks"
    },
  ];

  const supportServices = [
    {
      title: "5-Year Career Mentorship",
      description: "Unlike traditional programs with short-term support, our career advocates work with participants for a full 5 years, providing continuous professional development."
    },
    {
      title: "Financial Literacy & Benefits",
      description: "Training in personal finance management, savings strategies, and navigating benefits for financial independence. Includes debt and credit counseling."
    },
    {
      title: "Housing Stability Program",
      description: "We connect participants with stable housing options and provide transitional support to ensure that housing insecurity doesn't interrupt career development."
    },
    {
      title: "Recovery-Focused Environment",
      description: "All StaffAbility programs are built with recovery principles in mind, creating supportive communities for participants with addiction histories."
    }
  ];

  return (
    <section id="programs" className="py-16 bg-gradient-to-b from-white to-primary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:text-center">
          <div className="flex justify-center mb-2">
            <Badge variant="outline" className="bg-primary-50 text-primary-700 border-primary-200 px-3 py-1 text-sm">
              ✦ Concept Paper ✦
            </Badge>
          </div>
          <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">Skills Training Pathways</h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Building Skills for Meaningful Careers
          </p>
          <p className="mt-4 max-w-2xl text-xl text-slate-600 lg:mx-auto">
            StaffAbility proposes a comprehensive model combining intensive skills training with long-term career support.
          </p>
        </div>

        <div className="mt-12 mb-16">
          <h3 className="text-2xl font-bold text-slate-900 mb-8 lg:text-center">Proposed Training Programs</h3>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {trainingPrograms.map((program, index) => (
              <div key={index} className="bg-white overflow-hidden shadow-md rounded-lg hover:shadow-lg transition-all border border-slate-100">
                <div className="px-6 py-5 bg-primary-50 flex items-center border-b border-primary-100">
                  <div className="p-2 bg-white rounded-md shadow-sm mr-3">
                    {program.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-primary-900">{program.title}</h3>
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {program.categories.map((cat, idx) => (
                        <span key={idx} className="px-2 py-0.5 text-xs bg-primary-100 text-primary-800 rounded-full">
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="px-6 py-5">
                  <p className="text-slate-500 mb-3">
                    {program.description}
                  </p>
                  <div className="flex items-center text-primary-700 text-sm font-medium">
                    <GraduationCapIcon className="h-4 w-4 mr-1" />
                    <span>Duration: {program.duration}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-md border border-primary-100 mt-8">
          <h3 className="text-2xl font-bold text-slate-900 mb-6 lg:text-center">Complementary Support Services</h3>
          <p className="text-slate-600 mb-8 max-w-3xl mx-auto lg:text-center">
            Training alone isn't enough. The StaffAbility model includes these key support services to ensure long-term success:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {supportServices.map((service, index) => (
              <div key={index} className="flex gap-4 p-4 border border-slate-100 rounded-lg">
                <div className="rounded-full p-3 bg-primary-50 h-fit">
                  <UsersIcon className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <h4 className="font-medium text-primary-900 mb-1">{service.title}</h4>
                  <p className="text-sm text-slate-600">{service.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link href="/programs">
            <Button className="mr-3">Explore Our Approach</Button>
          </Link>
          <Link href="/get-involved">
            <Button variant="outline">Partnership Opportunities</Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
